import React, { useMemo } from 'react';
import { BodyText, FlexRow, FlexCol, H2Text, BodyTextLarge, BodyTextSmall, PrimaryButton } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import Icon from '@minecraft.icon';
import Pill from '@minecraft.pill';
import Tooltip from '@minecraft.uncontrolled-tooltip';

type StylishTitleProps = {
  title: string;
  className?: string;
  testId?: string;
};

const StylishTitle = ({ title, className, testId }: StylishTitleProps) => (
  <FlexRow alignItems="center" my="2" gap="4" className={className} data-testid={testId}>
    <FlexCol grow={0}>
      <BodyTextLarge as="h5">{title}</BodyTextLarge>
    </FlexCol>
    <FlexCol w="max" grow={1}>
      <FlexCol w="full" wrap="nowrap" alignItems="center" borderSize={0.07} bgColor="card" borderPlacement="top" />
    </FlexCol>
  </FlexRow>
);

const LabelAndValue = ({
  label,
  value,
  additionalValue,
  testId,
}: {
  label: string;
  value: string;
  additionalValue?: string;
  testId?: string;
}) => {
  if (!value) {
    return null;
  }

  return (
    <FlexCol w="full" gap="1" wrap="nowrap" data-testid={testId}>
      <BodyTextSmall fontWeight="medium" color="disabled">
        {label}
      </BodyTextSmall>
      <BodyText className="cn_white-space-pre-wrap">{value}</BodyText>
      {additionalValue && <BodyText>{additionalValue}</BodyText>}
    </FlexCol>
  );
};

type PillWithTooltipProps = {
  id: string;
  text: string;
  tooltipText: string;
  dataTestId: string;
  variant?: 'default' | 'danger';
  isLabelOnly?: boolean;
};

export const PillWithTooltip = ({
  id,
  text,
  tooltipText,
  dataTestId,
  variant = 'default',
  isLabelOnly = true,
}: PillWithTooltipProps) => {
  if (!text) {
    return null;
  }

  return (
    <>
      <div id={id} data-testid={dataTestId} data-tooltip-target={id}>
        <Pill outlined size="lg" isLabelOnly={isLabelOnly} variant={variant} text={text} />
      </div>
      {tooltipText && (
        <Tooltip placement="top" target={`[data-tooltip-target="${id}"]`} data-testid={`${dataTestId}-tooltip`}>
          <BodyTextSmall color="inverted">{tooltipText}</BodyTextSmall>
        </Tooltip>
      )}
    </>
  );
};

export type FormattedRoleData = {
  auditionLocationDates: string;
  callbackLocationDates: string;
  description: string;
  ethnicAppearance: string;
  fittingsLocationDates: string;
  genderAppearance: string;
  genderTooltipText: string;
  id: string;
  name: string;
  rateNote: string;
  rateSummary: string;
  requestingSubmissionsFrom: string;
  roleAge: string;
  roleEthnicity: string;
  roleEthnicityNationality: string;
  roleEthnicityTooltipText: string;
  roleRateSummary: string;
  roleType: string;
  roleUnion: string;
  sexualSituationsText: string;
  skills: string;
  submissionDueDate: string;
  submissionInstruction: string;
  wardrobe: string;
  workingLocationDates: string;
  workRequirement: string;
};

export const rolePills = (role: Partial<FormattedRoleData>) => [
  {
    field: 'roleType',
    text: role?.roleType,
  },
  {
    field: 'age',
    text: role?.roleAge,
  },
  {
    field: 'gender',
    text: role?.genderAppearance,
    tooltipText: role?.genderTooltipText,
  },
  {
    field: 'ethnicity',
    text: role?.roleEthnicity,
    tooltipText: role?.roleEthnicityTooltipText,
  },
  {
    field: 'sexualSituations',
    text: role?.sexualSituationsText,
  },
  {
    field: 'union',
    text: role?.roleUnion,
  },
  {
    field: 'rate',
    text: role?.roleRateSummary,
  },
];

export type RoleDetailsProps = {
  role: FormattedRoleData;
  showName?: boolean;
  showSummary?: boolean;
  showDetails?: boolean;
  showRoleRate?: boolean;
  showRoleDatesAndLocations?: boolean;
  showSubmissionInfo?: boolean;
  summaryButton?: { label: string; onClick: () => void };
};

export const RoleDetails = ({
  role,
  showName = true,
  showDetails = true,
  showSummary = true,
  showRoleRate = true,
  showRoleDatesAndLocations = true,
  showSubmissionInfo = true,
  summaryButton,
}: RoleDetailsProps) => {
  const { t } = useTranslation();
  const showAnyPills =
    role?.roleType ||
    role?.roleAge ||
    role?.genderAppearance ||
    role?.roleEthnicity ||
    role?.sexualSituationsText ||
    role?.roleUnion ||
    role?.roleRateSummary;
  const hasAnySubmissionInfo =
    role?.submissionDueDate || role?.submissionInstruction || role?.requestingSubmissionsFrom;
  const hasDatesAndLocations =
    role?.auditionLocationDates ||
    role?.callbackLocationDates ||
    role?.fittingsLocationDates ||
    role?.workingLocationDates;

  const formattedLocationDates = [
    {
      title: t('common:roleDetails.Audition'),
      text: role?.auditionLocationDates,
    },
    {
      title: t('common:roleDetails.Callback'),
      text: role?.callbackLocationDates,
    },
    {
      title: t('common:roleDetails.Fitting'),
      text: role?.fittingsLocationDates,
    },
    {
      title: t('common:roleDetails.Working'),
      text: role?.workingLocationDates,
    },
  ];

  const displayName = showName && typeof role === 'object' && role?.name;
  const pillsToRender = useMemo(() => {
    const allPills = rolePills(role);

    return allPills.filter((rolePill) => {
      const isRatePill = rolePill?.field === 'rate';
      return !isRatePill || (isRatePill && showRoleRate);
    });
  }, [role, showRoleRate]);

  return (
    <FlexCol gap="4" key={role?.id} w="full" data-testid={`role-details-wrapper-${role?.id}`}>
      {displayName && <H2Text>{role?.name}</H2Text>}

      {showSummary && (
        <>
          {showAnyPills && (
            <FlexRow data-testid="role-pills-wrapper" gap="2" w="full" wrap="nowrap">
              <FlexRow grow={1} shrink={2} gap="2">
                {pillsToRender.map((pill, index) => (
                  <PillWithTooltip
                    key={`${role?.id}-${pill?.field}`}
                    id={`${role?.id}-pill-${pill?.field}-${index}`}
                    text={pill?.text}
                    tooltipText={pill?.tooltipText}
                    dataTestId={`pill-${pill?.field}`}
                    isLabelOnly
                    variant={pill?.field === 'sexualSituations' ? 'danger' : 'default'}
                  />
                ))}
              </FlexRow>
              {summaryButton && (
                <FlexRow grow={0} shrink={0}>
                  <PrimaryButton label={summaryButton?.label} onClick={() => summaryButton?.onClick?.()} />
                </FlexRow>
              )}
            </FlexRow>
          )}

          {role?.submissionDueDate && showSubmissionInfo && (
            <FlexRow gap="2" data-testid="role-details-submission-date" alignItems="center" wrap="nowrap">
              <Icon name="schedule" />
              <BodyText fontWeight="medium">
                {t('common:roleDetails.submissionsDue')}: {role?.submissionDueDate}
              </BodyText>
            </FlexRow>
          )}
          {role?.description && <BodyText className="cn_white-space-pre-wrap">{role?.description}</BodyText>}
        </>
      )}
      {showDetails && (
        <>
          {role?.ethnicAppearance && (
            <LabelAndValue
              label={t('common:roleDetails.ethinicAppearance')}
              value={role?.ethnicAppearance}
              testId="ethnic-appearance"
            />
          )}
          {role?.roleEthnicityNationality && (
            <LabelAndValue
              label={t('common:roleDetails.ethnicityNationality')}
              value={role?.roleEthnicityNationality}
              testId="ethnicity-nationality"
            />
          )}
          {(role?.rateSummary || role?.rateNote) && showRoleRate && (
            <LabelAndValue
              label={t('common:roleDetails.rateDeatils')}
              value={role?.rateSummary || role?.rateNote}
              additionalValue={role?.rateSummary ? role?.rateNote : ''}
              testId="rate-details"
            />
          )}
          {role?.skills && (
            <LabelAndValue
              label={t('common:roleDetails.recommendedSkills')}
              value={role?.skills}
              testId="recommended-skills"
            />
          )}
          {role?.workRequirement && (
            <LabelAndValue
              label={t('common:roleDetails.workRequirements')}
              value={role?.workRequirement}
              testId="work-requirements"
            />
          )}
          {role?.wardrobe && (
            <LabelAndValue label={t('common:roleDetails.wardrobe')} value={role?.wardrobe} testId="wardrobe" />
          )}

          {hasDatesAndLocations && showRoleDatesAndLocations && (
            <FlexCol data-testid="dates-and-locations-wrapper" w="full" gap="4">
              <StylishTitle
                className="cn_w-full"
                title={t('common:roleDetails.datesAndLocations')}
                testId="dates-and-locations-title"
              />
              {formattedLocationDates.map((location) => (
                <LabelAndValue
                  key={location?.title}
                  label={location?.title}
                  value={location?.text}
                  testId={`location-date-${location?.title}`}
                />
              ))}
            </FlexCol>
          )}

          {hasAnySubmissionInfo && showSubmissionInfo && (
            <FlexCol data-testid="submission-info-wrapper" w="full" gap="4">
              <StylishTitle
                className="cn_w-full"
                title={t('common:roleDetails.submissionInformation')}
                testId="submission-information"
              />
              <LabelAndValue
                label={t('common:roleDetails.submissionsDueBy')}
                value={role?.submissionDueDate}
                testId="submissions-due-by"
              />
              {role?.submissionInstruction && (
                <LabelAndValue
                  label={t('common:roleDetails.submissionNoteInstructions')}
                  value={role?.submissionInstruction}
                  testId="submission-note-instructions"
                />
              )}
              {role?.requestingSubmissionsFrom && (
                <LabelAndValue
                  label={t('common:roleDetails.requestingSubmissionFrom')}
                  value={role?.requestingSubmissionsFrom}
                  testId="requesting-submission-from"
                />
              )}
            </FlexCol>
          )}
        </>
      )}
    </FlexCol>
  );
};
